<script setup>
  import { computed } from "vue";
  import { useRoute, useRouter } from "vue-router";

  import { WORKSPACE_EXAMS } from "@/config/workspace";
  import { useWorkspaceStore } from "@/stores/workspace";

  // 전체 메뉴(메가 메뉴, US-9500) — GNB 2행 호버 · ☰ · [기술지원] 클릭 시 헤더 아래로 펼친다
  const props = defineProps({
    isLoggedIn: { type: Boolean, default: false },
    isStudent: { type: Boolean, default: false },
    isWorkspace: { type: Boolean, default: false },
  });
  const emits = defineEmits(["close", "requireLogin"]);

  const route = useRoute();
  const router = useRouter();
  const ws = useWorkspaceStore();

  const examLinks = [
    { title: "문해력·수리력 진단검사(S-PLAN)", path: "/exam/s-plan/overview" },
    { title: "AI·디지털 리터러시 진단검사", path: "/exam/ai-literacy/overview" },
    { title: "AI·서논술형 평가(채움AI)", path: "/exam/essay/overview" },
  ];

  // 나의 검사 관리: 시험을 고른 채 업무 화면으로 (업무 화면 안에서는 현재 메뉴 유지, 시험만 전환)
  const myTestLinks = computed(() =>
    (props.isWorkspace ? ws.assignedExams : WORKSPACE_EXAMS).map((exam) => ({
      title: exam.label,
      path: "/my-test",
      examType: exam.type,
      requiresLogin: true,
    })),
  );

  // 진단검사 기술지원 · 1:1 문의는 교사 전용 (비로그인/학생은 공지사항만)
  const supportLinks = computed(() => {
    const links = [{ title: "공지사항", path: "/board/notice" }];
    if (props.isLoggedIn && !props.isStudent) {
      links.push(
        { title: "진단검사 기술지원", path: "/board/tech-support" },
        { title: "1:1 문의", path: "/board/inquiry" },
      );
    }
    return links;
  });

  const columns = computed(() => [
    { title: "검사 안내", prefix: "/exam", links: examLinks },
    { title: "나의 검사 관리", prefix: "/my-test", links: myTestLinks.value },
    { title: "기술지원", prefix: "/board", links: supportLinks.value },
  ]);

  // 나의 검사 관리 항목은 업무 화면에서 선택된 시험을 현재 위치로 표시한다
  const isCurrent = (link) =>
    link.examType
      ? props.isWorkspace && ws.examType === link.examType
      : route.path.startsWith(link.path);
  const isColumnActive = (col) => route.path.startsWith(col.prefix);

  const go = (link) => {
    emits("close");
    if (link.requiresLogin && !props.isLoggedIn) {
      emits("requireLogin");
      return;
    }
    if (link.examType) ws.setExamType(link.examType);
    if (link.examType && props.isWorkspace) return;
    router.push(link.path);
  };
</script>

<template>
  <div class="nh-mega" role="region" aria-label="전체 메뉴">
    <div class="nh-mega__inner">
      <div class="nh-mega__cols">
        <section
          v-for="col in columns"
          :key="col.title"
          class="nh-mega__col"
          :class="{ 'is-active': isColumnActive(col) }"
        >
          <h3>
            {{ col.title }}
            <em v-if="isColumnActive(col)">현재 메뉴</em>
          </h3>
          <ul>
            <li v-for="link in col.links" :key="`${col.title}-${link.title}`">
              <a
                href="#none"
                :class="{ 'is-current': isCurrent(link) }"
                :aria-current="isCurrent(link) ? 'page' : undefined"
                @click.prevent="go(link)"
              >
                {{ link.title }}
              </a>
            </li>
          </ul>
        </section>
      </div>

      <aside class="nh-mega__side">
        <a
          class="nh-mega__banner"
          href="#none"
          @click.prevent="go({ path: '/exam/essay/overview' })"
        >
          <small>검사 안내</small>
          <b>AI·서논술형 평가(채움AI)<br />시행 방법과 일정을 확인하세요</b>
          <span class="go">검사 안내 바로가기 →</span>
        </a>
        <div class="nh-mega__cs">
          <b>기술 지원</b>
          <span class="tel">070-4077-4425</span>
          <small>whaleubt@nsdevil.com<br />평일 09:00~18:00 (점심 12:00~13:00)</small>
        </div>
      </aside>
    </div>
  </div>
</template>

<style lang="scss" scoped>
  .nh-mega {
    position: absolute;
    left: 0;
    right: 0;
    top: 100%;
    background: #fff;
    border-top: 1px solid #e2e6ea;
    // 헤더 아래 화면 전체를 어둡게 덮는다 (위쪽은 clip-path 로 잘라 헤더는 밝게 유지)
    box-shadow:
      0 0 0 100vmax rgba(14, 24, 44, 0.28),
      0 20px 40px rgba(20, 40, 90, 0.12);
    clip-path: inset(0 -100vmax -100vmax -100vmax);
    animation: nhMegaIn 0.18s ease-out;

    &__inner {
      display: grid;
      grid-template-columns: minmax(0, 1fr) 300px;
      gap: 32px;
      width: 100%;
      max-width: 1280px;
      margin: 0 auto;
      padding: 32px 24px 36px;
    }

    &__cols {
      display: grid;
      grid-template-columns: minmax(0, 1fr) minmax(0, 1fr) minmax(0, 0.8fr);
    }

    &__col {
      padding: 0 26px;
      border-left: 1px solid #f0f2f4;

      &:first-child {
        border-left: 0;
        padding-left: 0;
      }

      &.is-active h3 {
        color: #0a7cff;
      }

      h3 em {
        margin-left: auto;
        padding: 2px 8px;
        border-radius: 60px;
        background: #e9f3ff;
        font-size: 11px;
        font-style: normal;
        font-weight: 600;
        color: #0a7cff;
      }

      h3 {
        display: flex;
        align-items: center;
        gap: 10px;
        min-height: 46px;
        margin-bottom: 14px;
        padding-bottom: 14px;
        border-bottom: 1px solid #f0f2f4;
        font-size: 16px;
        font-weight: 700;
        color: #1e2124;
      }

      a {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 8px;
        margin: 0 -10px 2px;
        padding: 8px 10px;
        border-radius: 8px;
        font-size: 14px;
        color: #464c53;

        &::after {
          content: "→";
          font-size: 12px;
          color: #0a7cff;
          opacity: 0;
          transform: translateX(-4px);
          transition: 0.15s;
        }

        &:hover {
          background: #f0f7ff;
          color: #0a7cff;

          &::after {
            opacity: 1;
            transform: none;
          }
        }

        &.is-current {
          background: #e9f3ff;
          color: #0a7cff;
          font-weight: 700;
        }
      }
    }

    &__side {
      display: flex;
      flex-direction: column;
      gap: 12px;
    }

    &__banner {
      position: relative;
      overflow: hidden;
      display: block;
      padding: 18px 18px 16px;
      border-radius: 16px;
      background: linear-gradient(135deg, #eef4ff 0%, #eaf1ff 55%, #e3f8f7 100%);
      box-shadow: inset 0 0 0 1px #dfe8fa;
      color: #1e2124;

      &::before {
        content: "";
        position: absolute;
        right: -30px;
        top: -30px;
        width: 120px;
        height: 120px;
        border-radius: 50%;
        background: radial-gradient(circle, rgba(63, 120, 251, 0.22), rgba(63, 120, 251, 0) 70%);
      }

      small {
        display: inline-block;
        margin-bottom: 8px;
        padding: 2px 8px;
        border-radius: 60px;
        background: #fff;
        font-size: 11px;
        font-weight: 700;
        color: #0a7cff;
      }

      b {
        position: relative;
        display: block;
        font-size: 15px;
        line-height: 1.45;
      }

      .go {
        position: relative;
        display: inline-flex;
        margin-top: 10px;
        font-size: 13px;
        font-weight: 600;
        color: #0a7cff;
      }

      &:hover .go {
        text-decoration: underline;
      }
    }

    &__cs {
      padding: 18px;
      border-radius: 16px;
      background: linear-gradient(100deg, #7b52f6 0%, #3f78fb 38%, #1893f3 70%, #2ad0d8 100%);
      color: #fff;

      b {
        display: block;
        font-size: 13px;
        opacity: 0.95;
      }

      .tel {
        display: block;
        margin: 4px 0 6px;
        font-size: 22px;
        font-weight: 800;
        letter-spacing: -0.3px;
      }

      small {
        display: block;
        font-size: 12px;
        line-height: 1.6;
        opacity: 0.9;
      }
    }
  }

  @keyframes nhMegaIn {
    from {
      opacity: 0;
      transform: translateY(-6px);
    }
    to {
      opacity: 1;
      transform: none;
    }
  }
</style>
